import { useRef, type ReactNode } from "react";
import * as Dialog from "@radix-ui/react-dialog";

import { Button } from "~/components";
import { tw } from "~/utils";

interface ConfirmDialogProps {
  show: boolean;
  title: string;
  description: ReactNode;
  children?: ReactNode;
  confirmLabel: string;
  isLoading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export const ConfirmDialog = ({
  show,
  title,
  description,
  children,
  confirmLabel,
  isLoading = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) => {
  const openerRef = useRef<Element | null>(null);

  return (
    <Dialog.Root open={show} onOpenChange={(open) => !open && onCancel()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 flex w-[calc(100dvw-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 flex-col gap-6 rounded-xl bg-white p-8 shadow-xl duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
          onOpenAutoFocus={() => {
            openerRef.current = document.activeElement;
          }}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            const opener = openerRef.current;
            if (
              opener instanceof HTMLElement &&
              opener !== document.body &&
              opener.isConnected
            ) {
              opener.focus();
              return;
            }

            if (document.activeElement !== document.body) return;

            document
              .querySelector<HTMLElement>('[role="dialog"][data-state="open"]')
              ?.focus();
          }}
        >
          <Dialog.Title className="font-ivy text-indigo text-[32px] font-light leading-10">
            {title}
          </Dialog.Title>

          <Dialog.Description className="text-dark-blue-gray text-base">
            {description}
          </Dialog.Description>

          {children}

          <div className="flex justify-between gap-4">
            <Button
              type="button"
              variant="outline"
              className={tw("flex-1", isLoading && "bg-white")}
              disabled={isLoading}
              onClick={onCancel}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="flex-1"
              isLoading={isLoading}
              onClick={onConfirm}
            >
              {confirmLabel}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
